import React, { useState, useEffect, useContext } from 'react';
import axios from '../api/axiosConfig';
import { AuthContext } from '../context/AuthContext';
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import './Dashboard.css'; // Usamos los mismos estilos base

const EmailMonitor = () => {
  const { user } = useContext(AuthContext);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [foundItems, setFoundItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [syncLoading, setSyncLoading] = useState(false);

  // Calcular resumen por trámite de los nuevos CUPLs
  const getNewItemsSummaryByTramite = () => {
    const newItems = foundItems.filter(item => !item.exists);
    const summary = newItems.reduce((acc, item) => {
      const tramite = item.tramite || 'Sin Trámite';
      acc[tramite] = (acc[tramite] || 0) + 1;
      return acc;
    }, {});
    return Object.entries(summary).sort((a, b) => b[1] - a[1]);
  };

  const newItemsSummary = getNewItemsSummaryByTramite();

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await axios.get('/api/automation/config');
      setConfig(response.data);
    } catch (err) {
      console.error('Error al obtener configuración:', err);
      setError('No se pudo cargar la configuración del monitor.');
    }
  };

  const handleTestConnection = async () => {
    setTestLoading(true);
    setMessage('');
    setError('');
    try {
      const response = await axios.get('/api/automation/test-connection');
      if (response.data.success) {
        setMessage('✅ ' + response.data.message);
      } else {
        setError('❌ Error de conexión: ' + response.data.message);
      }
    } catch (err) {
      setError('❌ Error al intentar conectar con el servidor de correo.');
    } finally {
      setTestLoading(false);
    }
  };

  const handleManualScan = async () => {
    setLoading(true);
    setMessage('');
    setError('');
    setFoundItems([]);
    setSummary(null);
    try {
      const response = await axios.post('/api/automation/scan-emails');
      console.log('Respuesta del backend:', response.data);
      setMessage(response.data.message);
      if (response.data.data) {
        console.log('Items encontrados:', response.data.data);
        setFoundItems(response.data.data);
      }
      if (response.data.summary) {
        setSummary(response.data.summary);
      }
    } catch (err) {
      console.error('Error en escaneo manual:', err);
      // Capturar el mensaje de error del backend si existe
      const errorMsg = err.response?.data?.message || 'Error al iniciar el escaneo manual.';
      setError('❌ ' + errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const [importLoading, setImportLoading] = useState(false);

  const handleBulkImport = async () => {
    if (foundItems.length === 0) {
      setError('No hay CUPLs para importar');
      return;
    }

    setImportLoading(true);
    setMessage('');
    setError('');
    
    try {
      const response = await axios.post('/api/automation/bulk-import', {
        items: foundItems.filter(item => !item.exists)
      });
      
      setMessage(`✅ ${response.data.message}`);
      
      // Actualizar la lista para mostrar que ya fueron importados o actualizados
      const updatedItems = foundItems.map(item => ({
        ...item,
        exists: item.exists || response.data.imported.includes(item.cupl) || response.data.updated.includes(item.cupl),
        hasChanges: response.data.updated.includes(item.cupl) ? false : item.hasChanges
      }));
      setFoundItems(updatedItems);
      
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Error al importar CUPLs';
      setError('❌ ' + errorMsg);
    } finally {
      setImportLoading(false);
    }
  };

  const handleSyncPDFs = async () => {
    if (!window.confirm('¿Deseas sincronizar los archivos PDF locales con la base de datos? Esto insertará los CUPLs que ya tienes descargados pero que no aparecen en el sistema.')) {
      return;
    }

    setSyncLoading(true);
    setMessage('');
    setError('');

    try {
      const response = await axios.post('/api/automation/sync-pdfs-to-db');
      setMessage(`✅ ${response.data.message}`);
      
      // Refrescar el escaneo para ver los cambios reflejados
      setTimeout(() => handleManualScan(), 2000);
      
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Error durante la sincronización';
      setError('❌ ' + errorMsg);
    } finally {
      setSyncLoading(false);
    }
  };

  return (
    <div className="app-layout">
      <Sidebar userRole={user?.role} />
      
      <div className="main-content">
        <Navbar title="📧 Monitor de Correo" />

        <div className="page-content">
          <header className="dashboard-header" style={{ marginBottom: '30px' }}>
            <h2 style={{ fontSize: '24px', color: '#1e293b' }}>Automatización de Pagos</h2>
            <p style={{ color: '#64748b' }}>Monitoreo de archivos PDF recibidos por correo electrónico.</p>
          </header>

          <div className="stats-grid">
            <div className="stat-card success">
              <div className="stat-icon">✅</div>
              <div className="stat-details">
                <span className="stat-label">Estado</span>
                <span className="stat-value" style={{ color: '#059669' }}>Activo</span>
                <small style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Escaneo Manual</small>
              </div>
            </div>
            <div className="stat-card info">
              <div className="stat-icon">📧</div>
              <div className="stat-details">
                <span className="stat-label">Filtro Remitente</span>
                <span className="stat-value" style={{ fontSize: '16px', wordBreak: 'break-all' }}>
                  {config ? config.email_filter : '...'}
                </span>
              </div>
            </div>
            <div className="stat-card warning">
              <div className="stat-icon">🔄</div>
              <div className="stat-details">
                <span className="stat-label">Sincronización PDF</span>
                <span className="stat-value" style={{ fontSize: '16px' }}>
                  {summary?.sync ? (
                    <span style={{ color: summary.sync.missingFiles.length > 0 ? '#dc2626' : '#059669' }}>
                      {summary.sync.totalFiles} archivos / {summary.sync.totalDb} en DB
                    </span>
                  ) : '...'}
                </span>
                {summary?.sync && (summary.sync.missingFiles.length > 0 || summary.sync.missingInDb.length > 0) && (
                  <div style={{ fontSize: '11px', color: '#dc2626', marginTop: '5px', maxHeight: '100px', overflowY: 'auto', border: '1px solid #fee2e2', padding: '5px', borderRadius: '4px', backgroundColor: '#fff' }}>
                    <p style={{ fontWeight: 'bold', margin: '0 0 3px 0' }}>⚠️ Alertas de Sincronización:</p>
                    {summary.sync.missingFiles.length > 0 && (
                      <div style={{ marginBottom: '5px' }}>
                        <strong style={{ display: 'block' }}>Faltan PDF en disco ({summary.sync.missingFiles.length}):</strong>
                        <div style={{ color: '#ef4444', wordBreak: 'break-all', marginTop: '2px' }}>
                          {summary.sync.missingFiles.join(', ')}
                        </div>
                      </div>
                    )}
                    {summary.sync.missingInDb.length > 0 && (
                      <div>
                        <strong style={{ display: 'block' }}>No están en DB ({summary.sync.missingInDb.length}):</strong>
                        <div style={{ color: '#2563eb', wordBreak: 'break-all', marginTop: '2px' }}>
                          {summary.sync.missingInDb.join(', ')}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="table-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0 }}>Acciones de Control</h2>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button 
                  className="btn-primary" 
                  onClick={handleManualScan}
                  disabled={loading || testLoading}
                >
                  {loading ? '🔍 Escaneando...' : '🔍 Escaneo Manual'}
                </button>
                <button 
                  className="btn-secondary" 
                  onClick={handleTestConnection}
                  disabled={loading || testLoading || syncLoading}
                  style={{ background: '#64748b' }}
                >
                  {testLoading ? '⚡ Probando...' : '⚡ Probar Conexión'}
                </button>
                <button 
                  className="btn-primary" 
                  onClick={handleSyncPDFs}
                  disabled={loading || testLoading || syncLoading || !summary?.sync || summary.sync.missingInDb.length === 0}
                  style={{ background: '#7c3aed' }}
                  title="Insertar CUPLs faltantes en DB que ya tienen PDF"
                >
                  {syncLoading ? '🔄 Sincronizando...' : '🔄 Sincronizar DB'}
                </button>
              </div>
            </div>

            {message && (
              <div style={{ padding: '15px', backgroundColor: '#dcfce7', color: '#166534', borderRadius: '8px', marginBottom: '20px', border: '1px solid #bbf7d0' }}>
                <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>{message}</div>
                {summary && (
                  <div style={{ fontSize: '14px' }}>
                    📊 <strong>Resumen General:</strong> {summary.total} total | 
                    <span style={{ color: '#059669' }}> {summary.existing} ya existen</span> | 
                    <span style={{ color: '#2563eb' }}> {summary.new} nuevos</span>
                  </div>
                )}
                {newItemsSummary.length > 0 && (
                  <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px dashed #bbf7d0' }}>
                    <div style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '8px', color: '#166534' }}>
                      📋 Nuevos CUPLs por Trámite:
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '8px' }}>
                      {newItemsSummary.map(([tramite, count]) => (
                        <div key={tramite} style={{ fontSize: '12px', backgroundColor: 'rgba(255,255,255,0.5)', padding: '6px 10px', borderRadius: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginRight: '10px' }} title={tramite}>
                            {tramite}
                          </span>
                          <span style={{ fontWeight: 'bold', backgroundColor: '#2563eb', color: 'white', padding: '2px 6px', borderRadius: '10px', fontSize: '10px' }}>
                            {count}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {error && (
              <div className="error-banner" style={{ marginBottom: '20px' }}>
                {error}
              </div>
            )}

            {foundItems.length > 0 ? (
              <div style={{ marginTop: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                  <h3 style={{ margin: 0, color: '#1e293b' }}>CUPLs Detectados en Bandeja</h3>
                  <button 
                    className="btn-primary" 
                    onClick={handleBulkImport}
                    disabled={importLoading || foundItems.filter(item => !item.exists).length === 0}
                  >
                    {importLoading ? '📥 Importando...' : '📥 Importar'}
                  </button>
                </div>
                <div className="table-wrapper">
                  <div className="table-responsive">
                    <table className="cupl-table">
                    <thead>
                      <tr>
                        <th>Número de CUPL</th>
                        <th>Estado DB</th>
                        <th>PDF Local</th>
                        <th>Placa</th>
                        <th>Nombre / Cliente</th>
                        <th>Valor PDF</th>
                        <th>Trámite</th>
                        <th>Fecha del Correo</th>
                        <th>Asunto Original</th>
                      </tr>
                    </thead>
                    <tbody>
                      {foundItems.map((item, index) => (
                        <tr key={index} style={{ backgroundColor: item.hasChanges ? '#fff7ed' : 'transparent' }}>
                          <td data-label="Número de CUPL" style={{ fontWeight: 'bold', color: '#2563eb' }}>
                            {item.cupl}
                            {item.hasChanges && (
                              <div style={{ fontSize: '10px', color: '#ea580c', marginTop: '2px' }}>
                                ⚠️ Cambio detectado
                              </div>
                            )}
                          </td>
                          <td data-label="Estado DB">
                            {item.exists ? (
                              <span style={{ 
                                padding: '4px 8px', 
                                backgroundColor: item.isPaid ? '#dcfce7' : '#fef9c3', 
                                color: item.isPaid ? '#166534' : '#854d0e',
                                borderRadius: '12px',
                                fontSize: '12px',
                                fontWeight: 'bold'
                              }}>
                                ✅ Existe {item.isPaid ? '(Pagado)' : '(Pendiente)'}
                              </span>
                            ) : (
                              <span style={{ 
                                padding: '4px 8px', 
                                backgroundColor: '#f1f5f9', 
                                color: '#64748b',
                                borderRadius: '12px',
                                fontSize: '12px'
                              }}>
                                🆕 Nuevo
                              </span>
                            )}
                          </td>
                          <td data-label="PDF Local">
                            <span style={{ 
                              padding: '4px 8px', 
                              backgroundColor: item.hasFile ? '#dcfce7' : '#fee2e2', 
                              color: item.hasFile ? '#166534' : '#991b1b',
                              borderRadius: '4px', 
                              fontSize: '12px'
                            }}>
                              {item.hasFile ? '✅ Descargado' : '❌ Faltante'}
                            </span>
                          </td>
                          <td data-label="Placa" style={{ color: '#64748b' }}>{item.placa}</td>
                          <td data-label="Nombre / Cliente" style={{ fontWeight: '600', color: '#1e293b' }}>
                            {item.nombre}
                          </td>
                          <td data-label="Valor PDF" style={{ color: '#059669', fontWeight: 'bold' }}>
                            {item.valor ? `$${item.valor.toLocaleString('es-CO')}` : '-'}
                          </td>
                          <td data-label="Trámite" style={{ fontSize: '11px', maxWidth: '150px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={item.tramite}>
                            {item.tramite || '-'}
                          </td>
                          <td data-label="Fecha del Correo">{new Date(item.date).toLocaleString()}</td>
                          <td data-label="Asunto Original" style={{ fontSize: '0.85rem', color: '#64748b' }}>{item.subject}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                </div>
              </div>
            ) : !loading && (
              <div className="no-data" style={{ padding: '40px', background: '#f8fafc', borderRadius: '8px', border: '2px dashed #e2e8f0' }}>
                No se han detectado archivos PDF en los correos recientes de {config?.email_filter}.
              </div>
            )}
          </div>

          <div className="form-section" style={{ marginTop: '30px' }}>
            <h3>ℹ️ Información del Proceso</h3>
            <ul style={{ paddingLeft: '20px', marginTop: '10px', color: '#475569', lineHeight: '1.6' }}>
              <li>El sistema analiza los últimos 100 correos de los <strong>remitentes configurados</strong>.</li>
              <li>Busca archivos <strong>.pdf</strong> cuyo nombre sea el número de CUPL.</li>
              <li>Descarga automáticamente los archivos nuevos a la carpeta local.</li>
              <li>Soporta correos con <strong>múltiples archivos PDF</strong> adjuntos.</li>
              <li>Se incluyen correos <strong>leídos y no leídos</strong> automáticamente.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailMonitor;
